/**
 * Three-way merge of paragraph text.
 *
 * A redline was drafted against `textWhenProposed`. By the time it is shown or
 * applied the paragraph may read `textOnPage`, because someone kept editing. This
 * merge produces the text the paragraph should become: the live text with the
 * redline's edits carried over.
 *
 * Every text is cut into the redline diff's units
 * (`WordTokenizer.diffUnitsWithTrailingSeparators`): its whitespace words, with a
 * Japanese, Chinese or Thai run cut into that script's words. Two edits to different
 * words of one sentence written without spaces are then two edits, as they are in
 * English. Units are compared by `WordTokenizer.comparisonKey`, the engine's key.
 *
 * Both `textProposed` and `textOnPage` are diffed against `textWhenProposed`.
 * Hunks are then walked in original order, and overlapping hunks from the two
 * sides are grouped into one region. A region touched by one side takes that
 * side. In a region touched by both sides the redline's words win: the redline
 * is the edit still to be applied, so it lands on top of whatever the paragraph
 * says at the moment of application, and the colleague's words in that region
 * are what the engine then deletes as tracked changes, visible and reversible,
 * never silently. Two pure insertions at the same point are both kept in the
 * text, live first, because neither displaces the other, but when their words
 * differ the region counts as contested like any other: two redlines adding
 * different words at one spot is a decision for the fold, not a merge.
 *
 * The merged text is written in the characters of the texts it takes. Every unit
 * is written as its own text spells it. Every gap, the whitespace between two
 * units or at either end of the text, is a gap one of the texts holds at that
 * point (`gapAtJoint`): the redline's where the redline changed that gap from the
 * text it was drafted against, otherwise the live text's. So a tab, a
 * non-breaking space or a line break the redline wrote is written, the live
 * text's spacing stays wherever the redline kept the original's, the text keeps
 * its trailing whitespace, and nothing is written between two words of a
 * Japanese or Chinese run, where no text holds a space. Where no text holds any
 * separator between two units, nothing is written between them, in every script:
 * a redline drafted against an empty paragraph follows the colleague's
 * text there with nothing between.
 *
 * EXAMPLE
 *   textWhenProposed  the supplier shall deliver written notice
 *   textProposed   the supplier shall revised terms notice      (redline: deliver written -> revised terms)
 *   textOnPage      the supplier shall deliver formal notice     (colleague, after the draft: written -> formal)
 *   region        original words 4-5, touched by both
 *   result        the supplier shall revised terms notice
 *   the engine then redlines the live paragraph toward it: deletes "deliver",
 *   deletes the colleague's pending "formal", inserts "revised terms".
 *
 * EXAMPLE, a sentence written without spaces
 *   textWhenProposed  本製品の保証期間は一年とする。   units 本 製品 の 保証 期間 は 一年 と する 。
 *   textProposed      本商品の保証期間は一年とする。   (redline: 製品 -> 商品)
 *   textOnPage        本製品の保証期間は二年とする。   (colleague: 一年 -> 二年)
 *   result            本商品の保証期間は二年とする。   one region per side, none contested
 *
 * `threeWayMergeReport` also counts the regions both sides changed to different
 * words, where the redline's words replaced the live text's: the core's Apply all
 * folds two rules' redlines of one paragraph through this merge and reads the
 * count to know whether the merged text can be written as it is (no contested
 * region) or the edit model has to reconcile the paragraph. A gap the two texts
 * changed two ways is not counted: the redline's gap is written, as its words are.
 *
 * `threeWayMergeReport` stays a function by decision; its steps are sub-functions
 * declared before use. `threeWayMerge` is the text alone.
 */
import { wordDiff, type WordDiffHunk } from "./word-diff";
import { type DiffUnitWithTrailingSeparator, WordTokenizer } from "./word-tokenizer";

/**
 * The original boundaries a unit or a gap of one side stands at. Original
 * boundary `b` is the point between original units `b - 1` and `b`: 0 is the
 * original's start and its unit count is its end.
 *
 * EXAMPLE
 *   original  "Alpha beta gamma"   boundaries 0 Alpha 1 beta 2 gamma 3
 *   side      "Alpha gamma"        its "gamma" stands at 2..3; its gap " " at 1..2,
 *                                  one gap for the two the side's deletion of "beta" joined
 */
interface OriginalBoundarySpan {
    firstOriginalBoundary: number;
    lastOriginalBoundary: number;
}

/** A text of the merge cut into the redline diff's units. */
interface MergeText {
    text: string;
    units: DiffUnitWithTrailingSeparator[];
}

/**
 * The live text or the redline as the merge reads it: its units, its diff from
 * the original, and the original boundaries each of its units and gaps stands at.
 */
interface MergeSide extends MergeText {
    /** The side's diff from the original, equal runs included, in original order. */
    opsFromOriginal: WordDiffHunk[];
    originalSpanOfUnit: OriginalBoundarySpan[];
    /**
     * Gap `g` stands before unit `g`: gap 0 is the text's leading whitespace and gap
     * `units.length` its trailing whitespace. Null for a gap between two units the
     * side wrote itself, which stands at no original boundary.
     */
    originalSpanOfGap: Array<OriginalBoundarySpan | null>;
    /**
     * Per original boundary, the side's unit index there, read from the right: after
     * any words the side inserted at that boundary. A boundary inside words the side
     * replaced or deleted reads as the start of that change; the original's end as the
     * side's unit count.
     */
    unitIndexAfterInsertionAtOriginalBoundary: number[];
    /** The same read from the left: before any words the side inserted at that boundary. */
    unitIndexBeforeInsertionAtOriginalBoundary: number[];
}

/** A unit the merged text writes: the side it comes from and its index there. */
interface MergedUnit {
    side: MergeSide;
    unitIndex: number;
}

/**
 * The point between two units the merged text writes, or between its first unit
 * and its start, or its last unit and its end, and the original boundaries it
 * stands across.
 */
interface MergeJoint {
    leftUnit: MergedUnit | null;
    rightUnit: MergedUnit | null;
    firstOriginalBoundary: number;
    lastOriginalBoundary: number;
}

/** The side's unit-index span covered by the hunks absorbed into the current region; `touched` says the side changed the region at all. */
interface Absorbed {
    touched: boolean;
    firstUnitIndex: number;
    endUnitIndex: number;
}

export interface ThreeWayMergeInput {
    textWhenProposed: string;
    textProposed: string;
    textOnPage: string;
    /**
     * True when `textOnPage` is the document's own text: what the user typed and the
     * redlines already applied, not another pending redline folded in before this one.
     *
     * CONSEQUENCE
     *   an insertion of the page's right beside words the redline covers
     *   (`insertionStandsBesideCover`) stands on its own: written as the page has it,
     *   the redline's words written beside it, and never counted contested. The card was
     *   drawn over the page as it reads, so the user saw the two together.
     *
     * NULL (absent)
     *   false: the core's fold of two rules' redlines (`POST /api/redline/three-way-merge`),
     *   where the page's insertion is another redline's words, drafted to follow the
     *   words beside it, so it stays contested.
     */
    textOnPageIsTheDocumentsOwn?: boolean;
}

/**
 * True when an insertion at `insertionPoint` sits at either edge of the words
 * `[coverStart, coverEnd)` another edit covers, sharing none of them: right before
 * the first, or right after the last. All three are boundaries between the units
 * of the text both edits were drafted on.
 *
 * CASES
 *   - the other edit covers no word (an insertion too) -> false: two insertions at one
 *                                                          point are a different case
 *   - the point is the cover's start or its end        -> true
 *   - anywhere else                                    -> false
 *
 * EXAMPLE
 *   text "Licensor grants Licensee", the card covers "Licensor": [0,1)
 *   page inserts "HEREBY" at 1   -> true: right after the card's word
 *   page inserts "said" at 0     -> true: right before it
 *   card covers [0,2), point 1   -> false: inside the words the card rewrites
 */
function insertionStandsBesideCover(insertionPoint: number, coverStart: number, coverEnd: number): boolean {
    return coverStart < coverEnd && (insertionPoint === coverStart || insertionPoint === coverEnd);
}

/**
 * The merged text and what the merge had to decide: `contestedRegionCount` is how
 * many regions both texts changed to different words, where the redline's words
 * replaced the live text's. Zero means the two edits touched different words, or
 * made the same change, and the merged text is exactly both edits together.
 */
export interface ThreeWayMergeReport {
    textToWrite: string;
    contestedRegionCount: number;
}

export function threeWayMerge(input: ThreeWayMergeInput): string {
    return threeWayMergeReport(input).textToWrite;
}

/**
 * How many insertions of one side sit right after words the other side rewrote or
 * deleted: the insertion point equals the end of the other side's cover.
 *
 * WHY
 *   The walk joins a hunk to a region when it starts at the region's start or inside
 *   it, so an insertion at the region's end edge becomes a region of its own that only
 *   one side touched. The words it adds then run straight on from the other side's
 *   rewrite and blend with it, which is a collision the caller has to reconcile, not a
 *   clean fold.
 *
 * EXAMPLE
 *   textWhenProposed "consent, not to be unreasonably withheld, and remains"
 *   page             rewrites "not to be unreasonably withheld," to "which Customer may withhold in its sole discretion,"   cover [1,6)
 *   card             inserts "conditioned or delayed," before "and"                                                           point 6
 *   -> 1: the point 6 is the cover's end, so the merge would read "…sole discretion, conditioned or delayed,"
 */
function insertionsAgainstCoverEnds(insertingSideHunks: WordDiffHunk[], coveringSideHunks: WordDiffHunk[]): number {
    return insertingSideHunks.filter((insertion) =>
        insertion.beforeStart === insertion.beforeEnd
        && coveringSideHunks.some((cover) => cover.beforeStart < cover.beforeEnd && cover.beforeEnd === insertion.beforeStart)).length;
}

/** The key two units are compared by: the engine's same-word key, never written. */
function comparisonKeyOfUnit(unit: DiffUnitWithTrailingSeparator): string {
    return WordTokenizer.comparisonKey(unit.text);
}

/**
 * The whitespace a text holds before its unit `gapIndex`.
 *
 * CASES
 *   - the text has no unit              -> the whole text, which is all whitespace
 *   - gapIndex 0                        -> the text before its first unit
 *   - gapIndex is the unit count        -> the text after its last unit
 *   - otherwise                         -> the separator after unit `gapIndex - 1`
 *
 * EXAMPLE
 *   text " Alpha\tbeta. "   units "Alpha" 1..6, "beta." 7..12
 *   gap 0 -> text.slice(0, 1)  = " "
 *   gap 1 -> units[0]'s separator "\t"
 *   gap 2 -> text.slice(12)    = " "
 */
function gapTextOf(mergeText: MergeText, gapIndex: number): string {
    const { text, units } = mergeText;
    if (units.length === 0) return text;
    if (gapIndex === 0) return text.slice(0, units[0].startOffset);
    /**
     * EXAMPLE
     *   units.length = 2, gapIndex = 2 -> the trailing whitespace
     *   `units.length - 1` -> 1, the last unit, whose endOffset 12 starts it
     */
    if (gapIndex === units.length) return text.slice(units[units.length - 1].endOffset);
    /**
     * EXAMPLE
     *   gapIndex = 1 -> `gapIndex - 1` = 0, unit "Alpha", whose separator "\t" stands before unit 1
     */
    return units[gapIndex - 1].trailingSeparator;
}

/**
 * Maps an index in the original's units to the matching unit index on one
 * side, read from the maps `mergeSideOf` builds once per side. For a pure
 * insertion before word `originalIndex` the `end` edge of a region ending
 * there stops before the insertion, so the inserted words belong to the next
 * region rather than this one; the `start` edge lands after them.
 *
 * EXAMPLE
 *   insertion at original index 7, inserted side units [4, 5]
 *   end edge at 7   -> 4, before the inserted run
 *   start edge at 7 -> 6, the unit matching original word 7
 */
function positionOn(side: MergeSide, originalIndex: number, edge: "start" | "end"): number {
    return edge === "start" ? side.unitIndexAfterInsertionAtOriginalBoundary[originalIndex] : side.unitIndexBeforeInsertionAtOriginalBoundary[originalIndex];
}

/**
 * The live text or the redline cut into units and diffed against the original,
 * with the original boundaries each of its units and gaps stands at.
 *
 * CASES, per op of the side's diff from the original
 *   - equal    -> unit `afterStart + i` stands at original `beforeStart + i .. + 1`, and
 *                 every gap from `afterStart` to `afterEnd` at the original boundary as far in
 *   - insert   -> its units stand at the insertion point; its first gap and the gap after
 *                 its last unit stand there too; the gaps between its units at none
 *   - delete   -> no unit; its one gap stands at every original boundary it removed
 *   - replace  -> its units stand across the words they replace; its first gap at the
 *                 replaced words' start, the gap after its last unit at their end
 *
 * EXAMPLE
 *   original "Alpha beta gamma"   side "Alpha new gamma"   ops: equal 0..1, replace 1..2 -> 1..2, equal 2..3
 *   unit "new" stands at 1..2; gap 1 (" " before "new") at 1; gap 2 (" " after it) at 2
 */
function mergeSideOf(text: string, original: MergeText): MergeSide {
    const units = WordTokenizer.diffUnitsWithTrailingSeparators(text);
    const opsFromOriginal = wordDiff(original.units.map(comparisonKeyOfUnit), units.map(comparisonKeyOfUnit));
    const originalSpanOfUnit: OriginalBoundarySpan[] = [];
    /**
     * EXAMPLE
     *   units.length = 3 -> gaps 0, 1, 2, 3: before each unit and after the last, so `+ 1`
     */
    const originalSpanOfGap: Array<OriginalBoundarySpan | null> = Array.from({ length: units.length + 1 }, () => null);
    const markGapAtOriginalBoundary = (gapIndex: number, originalBoundary: number): void => {
        const spanSoFar = originalSpanOfGap[gapIndex];
        /**
         * CASES
         *   - the gap stands at no boundary yet -> this boundary alone
         *   - it stands at some already         -> widened to take this one in, as a deletion's gap does
         */
        originalSpanOfGap[gapIndex] = spanSoFar === null
            ? { firstOriginalBoundary: originalBoundary, lastOriginalBoundary: originalBoundary }
            : { firstOriginalBoundary: Math.min(spanSoFar.firstOriginalBoundary, originalBoundary), lastOriginalBoundary: Math.max(spanSoFar.lastOriginalBoundary, originalBoundary) };
    };
    /**
     * EXAMPLE
     *   3 original units -> boundaries 0, 1, 2, 3, so `+ 1`; every one starts at the side's
     *   unit count, which the original's end keeps, and the ops below overwrite the rest
     */
    const unitIndexAfterInsertionAtOriginalBoundary = Array.from({ length: original.units.length + 1 }, () => units.length);
    const unitIndexBeforeInsertionAtOriginalBoundary = Array.from({ length: original.units.length + 1 }, () => units.length);
    const originalBoundariesWithAnInsertion = new Set<number>();
    /**
     * WHEN the original and the side both hold no unit
     *   CONSEQUENCE
     *     the diff has no op, and the side's one gap, its whole text, stands at the original's one boundary
     */
    if (opsFromOriginal.length === 0) markGapAtOriginalBoundary(0, 0);
    for (const op of opsFromOriginal) {
        /**
         * WHEN the op inserts words at an original boundary
         *   CONSEQUENCE
         *     read from the left, the boundary is the unit before the inserted run; read from
         *     the right it stays the unit after it, which the op that holds the boundary sets
         *
         * EXAMPLE
         *   insert before 7..7 -> after 4..6: from the left 4; from the right 6, set by the op starting at 7
         */
        if (op.beforeStart === op.beforeEnd) {
            unitIndexBeforeInsertionAtOriginalBoundary[op.beforeStart] = op.afterStart;
            originalBoundariesWithAnInsertion.add(op.beforeStart);
        }
        /**
         * EXAMPLE
         *   equal before 10..13 -> after 12..15: boundary 11 -> 12 + (11 - 10) = 13
         *   replace before 4..6 -> after 4..5:   boundaries 4 and 5 -> 4, the replacement's start
         *
         * WHY the boundary from the left is set only where no insertion stands
         *   The ops come in original order, so an insertion at a boundary is visited before
         *   the op that holds the boundary, which must not overwrite the insertion's start.
         */
        for (let originalBoundary = op.beforeStart; originalBoundary < op.beforeEnd; originalBoundary++) {
            const unitIndexAtBoundary = op.tag === "equal" ? op.afterStart + (originalBoundary - op.beforeStart) : op.afterStart;
            unitIndexAfterInsertionAtOriginalBoundary[originalBoundary] = unitIndexAtBoundary;
            if (!originalBoundariesWithAnInsertion.has(originalBoundary)) unitIndexBeforeInsertionAtOriginalBoundary[originalBoundary] = unitIndexAtBoundary;
        }
        if (op.tag === "equal") {
            /**
             * EXAMPLE
             *   equal before 2..4 -> after 5..7
             *   unit 5 stands at 2..3, unit 6 at 3..4
             *   gaps 5, 6, 7 stand at boundaries 2, 3, 4: `<=` takes the gap after the run's last unit
             */
            for (let offsetInRun = 0; offsetInRun < op.afterEnd - op.afterStart; offsetInRun++) {
                originalSpanOfUnit[op.afterStart + offsetInRun] = { firstOriginalBoundary: op.beforeStart + offsetInRun, lastOriginalBoundary: op.beforeStart + offsetInRun + 1 };
            }
            for (let offsetInRun = 0; offsetInRun <= op.afterEnd - op.afterStart; offsetInRun++) markGapAtOriginalBoundary(op.afterStart + offsetInRun, op.beforeStart + offsetInRun);
            continue;
        }
        for (let unitIndex = op.afterStart; unitIndex < op.afterEnd; unitIndex++) {
            originalSpanOfUnit[unitIndex] = { firstOriginalBoundary: op.beforeStart, lastOriginalBoundary: op.beforeEnd };
        }
        markGapAtOriginalBoundary(op.afterStart, op.beforeStart);
        markGapAtOriginalBoundary(op.afterEnd, op.beforeEnd);
    }
    return { text, units, opsFromOriginal, originalSpanOfUnit, originalSpanOfGap, unitIndexAfterInsertionAtOriginalBoundary, unitIndexBeforeInsertionAtOriginalBoundary };
}

/**
 * The gap a side holds at a joint: the one stretch of its text between what
 * stands left of the joint and what stands right of it, when that stretch is a
 * gap and stands at no original boundary outside the joint's.
 *
 * CASES, the side's gap index left of the joint
 *   - the joint is the merged text's start          -> 0
 *   - the unit left of the joint is the side's      -> the gap after it
 *   - it is the other side's                        -> where the joint's first original boundary falls on this side
 *   (and right of the joint the same way, with the merged text's end at the side's last gap)
 *
 * NULL
 *   - the two indexes differ: the side holds units between them that the merged text
 *     does not write (words it inserted there, or words the other side's edit replaced)
 *   - the gap stands at a boundary outside the joint's: the side deleted words there
 *     that the merged text writes, so its gap does not stand between these two units
 *
 * EXAMPLE
 *   original "Alpha beta gamma", page "Alpha", card "Alpha new gamma"; the merged text writes the
 *   card's "new" right after the page's "Alpha", a joint at original boundary 1
 *   page: its gap 1 is its trailing "", standing at 1..3 for the "beta gamma" it deleted -> null
 *   card: its gap 1 is " ", standing at 1 -> " "
 */
function gapOfSideAtJoint(side: MergeSide, joint: MergeJoint): string | null {
    const { leftUnit, rightUnit } = joint;
    /**
     * CASES
     *   - no unit left of the joint          -> 0, the side's leading whitespace
     *   - the left unit is the side's own    -> `unitIndex + 1`: unit 3's gap after it is gap 4
     *   - it is the other side's             -> mapped through the side's diff
     */
    const leftGapIndex = leftUnit === null ? 0 : leftUnit.side === side ? leftUnit.unitIndex + 1 : positionOn(side, joint.firstOriginalBoundary, "end");
    /**
     * CASES
     *   - no unit right of the joint         -> the unit count, the side's trailing whitespace
     *   - the right unit is the side's own   -> its index: gap 4 stands before unit 4
     *   - it is the other side's             -> mapped through the side's diff
     */
    const rightGapIndex = rightUnit === null ? side.units.length : rightUnit.side === side ? rightUnit.unitIndex : positionOn(side, joint.lastOriginalBoundary, "start");
    if (leftGapIndex !== rightGapIndex) return null;
    const gapSpan = side.originalSpanOfGap[leftGapIndex];
    /**
     * WHEN the gap stands at no original boundary, or at one outside the joint's
     *   CONSEQUENCE
     *     the side holds no gap here: see NULL above.
     */
    if (gapSpan === null || gapSpan.firstOriginalBoundary < joint.firstOriginalBoundary || gapSpan.lastOriginalBoundary > joint.lastOriginalBoundary) return null;
    return gapTextOf(side, leftGapIndex);
}

/**
 * The gap a side holds at one original boundary, as it would stand between the
 * original's words there: null when the side inserted or removed words at that
 * boundary, or changed the words on either side of it.
 */
function gapOfSideAtOriginalBoundary(side: MergeSide, originalBoundary: number): string | null {
    const gapIndexFromTheLeft = positionOn(side, originalBoundary, "end");
    const gapIndexFromTheRight = positionOn(side, originalBoundary, "start");
    if (gapIndexFromTheLeft !== gapIndexFromTheRight) return null;
    const gapSpan = side.originalSpanOfGap[gapIndexFromTheLeft];
    /**
     * WHEN the gap stands at more boundaries than this one
     *   CONSEQUENCE
     *     the side deleted the words around this boundary: it holds no gap between them.
     */
    if (gapSpan === null || gapSpan.firstOriginalBoundary !== originalBoundary || gapSpan.lastOriginalBoundary !== originalBoundary) return null;
    return gapTextOf(side, gapIndexFromTheLeft);
}

/**
 * Which of the two sides' gaps the merged text writes at a joint.
 *
 * CASES, a joint at one original boundary, whose original gap is `originalGap`
 *   - both sides hold a gap   -> the redline's when it differs from the original's: the redline
 *                                changed it, beside its words or anywhere else; otherwise the
 *                                live text's, which keeps the colleague's spacing
 *   - one side holds one      -> that one
 *   - neither                 -> null
 *
 * CASES, a joint across original boundaries (a side deleted the words between them)
 *   - the redline holds the gap across     -> when it is the original gap at either end, the
 *                                             live text's gap at that end, else the live text's
 *                                             gap across when it holds one; otherwise the redline's
 *   - only the live text holds one         -> the redline's gap at either end when the redline
 *                                             changed it; otherwise the live text's
 *   - neither                              -> null
 *
 * EXAMPLE
 *   original "Street London", card "Street\nLondon", page "Street London": card "\n" differs from " " -> "\n"
 *   original "A B C", card "A\tC" (deleted "B"), page "A B C D": the card's "\t" across 1..2 is neither
 *   original gap -> "\t"
 *   original "A B C", card "A C", page "A\tB C": the card's " " is the original's gap at 1 -> the page's "\t"
 */
function chosenGapAtJoint(joint: MergeJoint, original: MergeText, liveSide: MergeSide, desiredSide: MergeSide): string | null {
    const redlineGap = gapOfSideAtJoint(desiredSide, joint);
    const liveGap = gapOfSideAtJoint(liveSide, joint);
    const { firstOriginalBoundary, lastOriginalBoundary } = joint;
    if (firstOriginalBoundary === lastOriginalBoundary) {
        const originalGap = gapTextOf(original, firstOriginalBoundary);
        if (redlineGap !== null && liveGap !== null) return redlineGap !== originalGap ? redlineGap : liveGap;
        return redlineGap ?? liveGap;
    }
    const originalGapAtFirst = gapTextOf(original, firstOriginalBoundary);
    const originalGapAtLast = gapTextOf(original, lastOriginalBoundary);
    if (redlineGap !== null) {
        const liveGapAtFirst = gapOfSideAtOriginalBoundary(liveSide, firstOriginalBoundary);
        if (redlineGap === originalGapAtFirst && liveGapAtFirst !== null) return liveGapAtFirst;
        const liveGapAtLast = gapOfSideAtOriginalBoundary(liveSide, lastOriginalBoundary);
        if (redlineGap === originalGapAtLast && liveGapAtLast !== null) return liveGapAtLast;
        const redlineKeptAnOriginalGap = redlineGap === originalGapAtFirst || redlineGap === originalGapAtLast;
        return redlineKeptAnOriginalGap && liveGap !== null ? liveGap : redlineGap;
    }
    if (liveGap === null) return null;
    const redlineGapAtFirst = gapOfSideAtOriginalBoundary(desiredSide, firstOriginalBoundary);
    if (redlineGapAtFirst !== null && redlineGapAtFirst !== originalGapAtFirst) return redlineGapAtFirst;
    const redlineGapAtLast = gapOfSideAtOriginalBoundary(desiredSide, lastOriginalBoundary);
    if (redlineGapAtLast !== null && redlineGapAtLast !== originalGapAtLast) return redlineGapAtLast;
    return liveGap;
}

/**
 * The joint between two units the merged text writes next to each other, or
 * between a unit and the text's start (`leftUnit` null) or end (`rightUnit` null).
 *
 * CASES
 *   - two units of one side, next to each other there -> the original boundaries their
 *                                                        gap stands at; a gap the side wrote
 *                                                        inside its own words stands at none
 *                                                        and is written as it is
 *   - anything else                                   -> from where the left unit ends to
 *                                                        where the right unit starts in the original
 *
 * EXAMPLE
 *   original "A B C"; the merged text writes the page's "A" (0..1) and its "C" (2..3) after the
 *   card deleted "B" -> the joint stands across 1..2
 */
function jointBetween(leftUnit: MergedUnit | null, rightUnit: MergedUnit | null, originalUnitCount: number): MergeJoint | string {
    const unitsStandSideBySideOnOneSide = leftUnit !== null && rightUnit !== null && leftUnit.side === rightUnit.side && rightUnit.unitIndex === leftUnit.unitIndex + 1;
    if (unitsStandSideBySideOnOneSide) {
        const gapSpan = rightUnit.side.originalSpanOfGap[rightUnit.unitIndex];
        /**
         * WHEN the gap between them is one the side wrote inside its own inserted or replacing words
         *   CONSEQUENCE
         *     no other text holds a gap there: it is written as the side wrote it
         */
        if (gapSpan === null) return gapTextOf(rightUnit.side, rightUnit.unitIndex);
        return { leftUnit, rightUnit, firstOriginalBoundary: gapSpan.firstOriginalBoundary, lastOriginalBoundary: gapSpan.lastOriginalBoundary };
    }
    /**
     * CASES
     *   - no unit on the left  -> 0, the original's start
     *   - otherwise            -> the original boundary the left unit's span ends at
     */
    const leftEndBoundary = leftUnit === null ? 0 : leftUnit.side.originalSpanOfUnit[leftUnit.unitIndex].lastOriginalBoundary;
    /**
     * CASES
     *   - no unit on the right -> the original's end, its unit count
     *   - otherwise            -> the original boundary the right unit's span starts at
     */
    const rightStartBoundary = rightUnit === null ? originalUnitCount : rightUnit.side.originalSpanOfUnit[rightUnit.unitIndex].firstOriginalBoundary;
    /**
     * WHY min and max
     *   Units are written in original order, so the left unit ends at or before the right one
     *   starts; the two are ordered here so a joint never stands across a reversed span.
     */
    return { leftUnit, rightUnit, firstOriginalBoundary: Math.min(leftEndBoundary, rightStartBoundary), lastOriginalBoundary: Math.max(leftEndBoundary, rightStartBoundary) };
}

/**
 * The text of a merged unit, as its side spells it.
 */
function textOfMergedUnit(mergedUnit: MergedUnit): string {
    return mergedUnit.side.units[mergedUnit.unitIndex].text;
}

export function threeWayMergeReport({ textWhenProposed, textProposed, textOnPage, textOnPageIsTheDocumentsOwn = false }: ThreeWayMergeInput): ThreeWayMergeReport {
    const original: MergeText = { text: textWhenProposed, units: WordTokenizer.diffUnitsWithTrailingSeparators(textWhenProposed) };
    const desiredSide = mergeSideOf(textProposed, original);
    const liveSide = mergeSideOf(textOnPage, original);
    const hunksWanted = desiredSide.opsFromOriginal.filter((op) => op.tag !== "equal");
    const hunksCurrent = liveSide.opsFromOriginal.filter((op) => op.tag !== "equal");
    const merged: MergedUnit[] = [];
    /** The indexes in `merged` of the units written by a region the colleague changed: its own words, not the original's untouched ones. */
    const mergedIndexesTheColleagueChanged = new Set<number>();
    /**
     * The gaps decided where one side inserted words at an original boundary, by the
     * index of the joint before or after those words (`decideGapsAroundInsertion`).
     */
    const gapDecidedAtJoint = new Map<number, string>();
    /**
     * Regions both sides changed to different words, counted as the walk resolves them,
     * starting from the insertions one side made right after words the other side
     * rewrote or deleted (`insertionsAgainstCoverEnds`), which the walk resolves as two
     * separate regions and so never counts.
     */
    let contestedRegionCount = insertionsAgainstCoverEnds(hunksWanted, hunksCurrent);
    /**
     * WHEN the page is another redline's words, not the document's own text
     *   CONSEQUENCE
     *     its insertions right after the redline's words count too: they were drafted to
     *     follow the words the redline rewrites (`insertionsAgainstCoverEnds`).
     *
     * WHY not for the document's own text
     *   The user's words beside a card's stand beside them, as the card was drawn
     *   (`textOnPageIsTheDocumentsOwn`).
     */
    if (!textOnPageIsTheDocumentsOwn) contestedRegionCount += insertionsAgainstCoverEnds(hunksCurrent, hunksWanted);

    /** True when two runs of merged units carry the same words in the same order, whatever their spacing. */
    const sameUnitWords = (left: MergedUnit[], right: MergedUnit[]): boolean =>
        left.length === right.length && left.every((mergedUnit, index) => WordTokenizer.comparisonKey(textOfMergedUnit(mergedUnit)) === WordTokenizer.comparisonKey(textOfMergedUnit(right[index])));
    /**
     * WHEN a side has not absorbed any hunk for this region yet
     *   CONSEQUENCE
     *     firstUnitIndex is Infinity
     *     endUnitIndex   is -Infinity
     *     touched         is false
     *
     * WHY
     *   The first absorbed hunk or mapped boundary replaces these sentinels via
     *   Math.min and Math.max.
     */
    const createUntouchedAbsorbedRegion = (): Absorbed => ({ touched: false, firstUnitIndex: Infinity, endUnitIndex: -Infinity });
    /** The side's units for original words `start..end`, widened to include every absorbed hunk. */
    const regionOf = (side: MergeSide, start: number, end: number, absorbed: Absorbed): MergedUnit[] => {
        /**
         * CASES
         *   - mapped region start comes first     -> start there
         *   - absorbed hunk unit comes first      -> start there
         *
         * WHY
         *   Insertions or replacements that begin before any surviving mapped word must
         *   stay inside the region.
         */
        const firstUnitIndex = Math.min(positionOn(side, start, "start"), absorbed.firstUnitIndex);
        /**
         * CASES
         *   - mapped region end reaches farther   -> end there
         *   - absorbed hunk reaches farther       -> end there
         *
         * WHY
         *   Insertions or replacements that extend past the mapped end must stay inside
         *   the region.
         */
        const endUnitIndex = Math.max(positionOn(side, end, "end"), absorbed.endUnitIndex);
        /**
         * EXAMPLE
         *   firstUnitIndex = 2
         *   endUnitIndex   = 5
         *   `endUnitIndex - firstUnitIndex` = 3 units: indexes 2, 3, 4
         *
         * EDGE CASE
         *   endUnitIndex <= firstUnitIndex (a region the side deleted) -> no unit
         */
        return Array.from({ length: Math.max(0, endUnitIndex - firstUnitIndex) }, (_, offsetInRegion) => ({ side, unitIndex: firstUnitIndex + offsetInRegion }));
    };

    /**
     * Guards against writing the redline's insertion twice where the colleague
     * already wrote it (a redline applied twice): the words written just before the
     * insertion point end with the colleague's own change and read as the redline's
     * inserted words.
     *
     * CASES
     *   - the redline adds no unit                                -> present: it adds nothing
     *   - the redline replaces or deletes original words here     -> missing: the live text still
     *                                                                holds them, so the change is not on the page
     *   - the units written last are live text, end with a unit a
     *     region the colleague changed wrote, and read as the
     *     redline's inserted words                                -> present
     *   - otherwise                                               -> missing: the redline's words are written
     *
     * WHY the last unit has to be the colleague's own
     *   An original word just before the point that reads as the inserted word is the text
     *   the redline was drafted against: a redline that repeats it ("had" -> "had had",
     *   研究 -> 研究研究) writes its own word, and the page holds nothing of it yet.
     *
     * EXAMPLE
     *   original "…the party making the Claim.", the redline appends " Claims must be made in good
     *   faith…", the page rewrote the paragraph and already ends with that sentence: the units
     *   written last are the page's, ending "…submission." from the page's own rewrite -> present
     *   original "The Party that had notice", redline "The Party that had had notice": the unit
     *   written last is the original's untouched "had" -> missing, "had had" written
     */
    const desiredRegionIsAlreadyPresentInLiveContext = (desiredRegionUnits: MergedUnit[], regionStart: number, regionEnd: number): boolean => {
        /**
         * WHEN the desired-side contribution has no units
         *   CONSEQUENCE
         *     Treat it as already present: an empty addition contributes nothing to the
         *     merged paragraph.
         */
        if (desiredRegionUnits.length === 0) return true;
        /**
         * WHEN the redline replaces or deletes original words in this region
         *   CONSEQUENCE
         *     missing: the live text still holds the words the redline replaces here.
         */
        if (regionStart !== regionEnd) return false;
        const mergedTailIsLongEnoughToCompare = merged.length >= desiredRegionUnits.length;
        /**
         * EXAMPLE
         *   merged.length              = 7
         *   desiredRegionUnits.length  = 2
         *   slice(-2)                  -> merged indexes 5 and 6
         */
        const mergedTailUnits = merged.slice(-desiredRegionUnits.length);
        const mergedTailIsLiveText = mergedTailUnits.every((mergedUnit) => mergedUnit.side === liveSide);
        /**
         * EXAMPLE
         *   merged.length = 7 -> `7 - 1` = 6, the unit written right before the insertion point
         */
        const lastWrittenUnitIsTheColleaguesChange = mergedIndexesTheColleagueChanged.has(merged.length - 1);
        return mergedTailIsLongEnoughToCompare && mergedTailIsLiveText && lastWrittenUnitIsTheColleaguesChange && sameUnitWords(mergedTailUnits, desiredRegionUnits);
    };

    /**
     * The gaps either side of words one side inserted at an original boundary where
     * the other side inserted nothing. The original held one gap there and the merged
     * text holds two: the edge where the inserting side kept the original's gap keeps
     * that joint's own choice (`chosenGapAtJoint`, which writes the other side's gap
     * there, or the redline's change of it), and the other edge is the inserting
     * side's own new gap.
     *
     * CASES, which edge the inserting side kept the original's gap at
     *   - before its words (the gap before them reads as the original's, the gap after does not) -> before
     *   - after its words                                                                         -> after
     *   - both read as the original's: before, unless the insertion ends the text, where the
     *     words were added after the last one and the trailing whitespace stays last
     *   - neither: the side replaced the original's gap with two of its own   -> both its own
     *
     * WHY
     *   Each joint alone picks the gap the other side holds at the boundary when the
     *   inserting side kept the original's there; both joints would then write it.
     *   "A C" with the page's "A\tC" and a card inserting "B" read "A\tB\tC", two tabs for one.
     *
     * EXAMPLE
     *   original "A C", page "A\tC", card "A B C"   -> the card kept " " before "B": the joint
     *   before writes the page's "\t", the joint after the card's own " ": "A\tB C"
     *   original "本製品", page "本製品 ", card "本製品の" -> the insertion ends the text: the
     *   joint before writes the card's "", the joint after the page's " ": "本製品の "
     */
    const decideGapsAroundInsertion = (insertingSide: MergeSide, insertedUnits: MergedUnit[], insertionBoundary: number, jointIndexBefore: number): void => {
        /**
         * EXAMPLE
         *   insertedUnits.length = 2, jointIndexBefore = 5 -> the joint after them is 7
         */
        const jointIndexAfter = jointIndexBefore + insertedUnits.length;
        const firstInsertedUnitIndex = insertedUnits[0].unitIndex;
        /**
         * EXAMPLE
         *   inserted units 4 and 5 -> gap 4 before them, gap `5 + 1` = 6 after them
         */
        const gapIndexAfterInsertion = insertedUnits[insertedUnits.length - 1].unitIndex + 1;
        const insertingSideGapBefore = gapTextOf(insertingSide, firstInsertedUnitIndex);
        const insertingSideGapAfter = gapTextOf(insertingSide, gapIndexAfterInsertion);
        const originalGap = gapTextOf(original, insertionBoundary);
        const insertionEndsTheText = insertionBoundary === original.units.length;
        const keptOriginalGapBefore = insertingSideGapBefore === originalGap && (insertingSideGapAfter !== originalGap || !insertionEndsTheText);
        const keptOriginalGapAfter = !keptOriginalGapBefore && insertingSideGapAfter === originalGap;
        /**
         * CASES
         *   - kept before -> the joint after is the side's own new gap
         *   - kept after  -> the joint before is
         *   - neither     -> both are
         */
        if (!keptOriginalGapBefore) gapDecidedAtJoint.set(jointIndexBefore, insertingSideGapBefore);
        if (!keptOriginalGapAfter) gapDecidedAtJoint.set(jointIndexAfter, insertingSideGapAfter);
    };

    /**
     * The loop alternates between copying untouched live text up to the next
     * hunk and resolving one region. A region starts at the nearest hunk and
     * keeps absorbing hunks from either side while they begin inside it, so any
     * chain of overlapping edits resolves as one unit.
     */
    let originalCursor = 0;
    let hunksWantedIndex = 0;
    let hunksCurrentIndex = 0;
    const originalWordCount = original.units.length;
    /**
     * WHEN either diff stream may still have a non-equal hunk left
     *   CONSEQUENCE
     *     Keep looping.
     *
     * WHY
     *   Each pass copies untouched live text up to the next original boundary, then
     *   resolves at least one region.
     */
    for (; ;) {
        const nextWanted = hunksWanted[hunksWantedIndex]?.beforeStart ?? Infinity;
        const nextCurrent = hunksCurrent[hunksCurrentIndex]?.beforeStart ?? Infinity;
        /**
         * EXAMPLE
         *   nextWanted        = 5
         *   nextCurrent       = 8
         *   originalWordCount = 12
         *   Math.min(...)     -> 5
         */
        const nextOriginalBoundary = Math.min(nextWanted, nextCurrent, originalWordCount);
        /**
         * WHEN there is a real original-word span before the next hunk boundary
         *   CONSEQUENCE
         *     Copy that untouched live text now.
         *
         * EDGE CASE
         *   originalCursor === nextOriginalBoundary
         *     -> the next event is an insertion point, so there are no original words to
         *        copy
         */
        if (originalCursor < nextOriginalBoundary) {
            const untouchedLiveRegionUnits = regionOf(liveSide, originalCursor, nextOriginalBoundary, createUntouchedAbsorbedRegion());
            merged.push(...untouchedLiveRegionUnits);
        }
        originalCursor = nextOriginalBoundary;
        const wantedStreamIsExhausted = nextWanted === Infinity;
        const currentStreamIsExhausted = nextCurrent === Infinity;
        const bothDiffStreamsAreExhausted = wantedStreamIsExhausted && currentStreamIsExhausted;
        /**
         * WHEN neither side has any non-equal hunk left
         *   CONSEQUENCE
         *     Stop: the remaining untouched live tail has already been copied, and no
         *     conflict region remains.
         */
        if (bothDiffStreamsAreExhausted) break;
        const regionStart = originalCursor;
        let regionEnd = originalCursor;
        const fromWanted = createUntouchedAbsorbedRegion();
        const fromCurrent = createUntouchedAbsorbedRegion();
        const absorbHunkIntoRegion = (hunk: WordDiffHunk, into: Absorbed): void => {
            /**
             * WHEN an absorbed hunk reaches farther right in original-word space
             *   CONSEQUENCE
             *     Extend the region to that boundary.
             *
             * WHY
             *   Later hunks that start inside this expanded span must resolve as part of the
             *   same overlap chain.
             */
            regionEnd = Math.max(regionEnd, hunk.beforeEnd);
            into.touched = true;
            into.firstUnitIndex = Math.min(into.firstUnitIndex, hunk.afterStart);
            into.endUnitIndex = Math.max(into.endUnitIndex, hunk.afterEnd);
        };
        const nextCurrentHunk = hunksCurrent[hunksCurrentIndex];
        const nextWantedHunk = hunksWanted[hunksWantedIndex];
        /**
         * WHEN the page is the document's own text and its next edit is an insertion at
         * this boundary, right before words the redline's next edit covers
         *   CONSEQUENCE
         *     the insertion is a region of its own, written as the page has it, and the
         *     redline's words are the next region: the user's words stand before the
         *     card's, as the card drew them, and nothing is contested. Read as one region,
         *     the redline won it and struck the user's words.
         *
         * EXAMPLE
         *   textWhenProposed "The Licensor grants"   textProposed "The Provider grants"
         *   textOnPage       "The said Licensor grants"
         *   page inserts "said" at 1, the redline covers [1,2) -> "The said Provider grants"
         */
        const pageInsertionStandsBeforeRedlineCover = textOnPageIsTheDocumentsOwn
            && nextCurrentHunk !== undefined
            && nextWantedHunk !== undefined
            && nextCurrentHunk.beforeStart === regionStart
            && nextCurrentHunk.beforeEnd === regionStart
            && nextWantedHunk.beforeStart === regionStart
            && insertionStandsBesideCover(regionStart, nextWantedHunk.beforeStart, nextWantedHunk.beforeEnd);
        if (pageInsertionStandsBeforeRedlineCover) {
            absorbHunkIntoRegion(nextCurrentHunk, fromCurrent);
            hunksCurrentIndex++;
        }
        /** The insertion standing before the redline's words is its whole region: nothing more joins it. */
        let regionExpanded = !pageInsertionStandsBeforeRedlineCover;
        while (regionExpanded) {
            regionExpanded = false;
            /**
             * CASES
             *   - the wanted side has no hunks left -> no
             *   - otherwise                         -> yes when the next hunk starts at or inside the region
             */
            const nextWantedHunkShouldBeAbsorbed = (): boolean => {
                const wantedHunksRemain = hunksWantedIndex < hunksWanted.length;
                if (!wantedHunksRemain) return false;
                const nextWantedHunk = hunksWanted[hunksWantedIndex];
                const nextWantedHunkStartsAtRegionStart = nextWantedHunk.beforeStart === regionStart;
                const nextWantedHunkStartsInsideRegion = nextWantedHunk.beforeStart < regionEnd;
                return nextWantedHunkStartsAtRegionStart || nextWantedHunkStartsInsideRegion;
            };
            /**
             * CASES
             *   - beforeStart === regionStart   -> absorb pure insertions at the region start
             *   - beforeStart < regionEnd       -> absorb non-zero-width hunks that start
             *                                      inside the region
             */
            while (nextWantedHunkShouldBeAbsorbed()) {
                absorbHunkIntoRegion(hunksWanted[hunksWantedIndex], fromWanted);
                hunksWantedIndex++;
                regionExpanded = true;
            }
            /**
             * CASES
             *   - the live side has no hunks left -> no
             *   - otherwise                       -> yes when the next hunk starts at or inside the region
             */
            const nextCurrentHunkShouldBeAbsorbed = (): boolean => {
                const currentHunksRemain = hunksCurrentIndex < hunksCurrent.length;
                if (!currentHunksRemain) return false;
                const nextCurrentHunk = hunksCurrent[hunksCurrentIndex];
                const nextCurrentHunkStartsAtRegionStart = nextCurrentHunk.beforeStart === regionStart;
                const nextCurrentHunkStartsInsideRegion = nextCurrentHunk.beforeStart < regionEnd;
                return nextCurrentHunkStartsAtRegionStart || nextCurrentHunkStartsInsideRegion;
            };
            /**
             * CASES
             *   - beforeStart === regionStart   -> absorb pure insertions at the region start
             *   - beforeStart < regionEnd       -> absorb non-zero-width hunks that start
             *                                      inside the region
             */
            while (nextCurrentHunkShouldBeAbsorbed()) {
                absorbHunkIntoRegion(hunksCurrent[hunksCurrentIndex], fromCurrent);
                hunksCurrentIndex++;
                regionExpanded = true;
            }
        }
        /**
         * WHEN both sides inserted at the same original boundary
         *   CONSEQUENCE
         *     Keep both insertions: the live one is emitted first, then the desired one.
         *
         * WHY
         *   Neither insertion replaces original words, so they can coexist.
         */
        const regionIsAnInsertionPoint = regionStart === regionEnd;
        const bothSidesInsertedAtRegionStart = fromCurrent.touched && fromWanted.touched && regionIsAnInsertionPoint;
        const liveSideOnlyTouchedRegion = fromCurrent.touched && !fromWanted.touched;
        /**
         * The joint before the region's first unit: its index is the count of units written before it.
         *
         * EXAMPLE
         *   merged.length = 6 -> the region's first unit is merged[6], joint 6 stands before it
         */
        const jointIndexBeforeRegion = merged.length;
        /**
         * CASES
         *   - live touched, desired untouched        -> the colleague's words stand
         *   - both inserted at the same start point  -> the colleague's insertion first, the redline's after it
         *   - both touched on original words         -> the colleague's words in the region are NOT copied:
         *                                               the redline's words replace them (below)
         *
         * WHY
         *   The redline is the edit still to be applied. It lands on top of the paragraph
         *   as it reads now; the engine turns the dropped colleague words into tracked
         *   deletions, so nothing is overwritten silently.
         */
        if (liveSideOnlyTouchedRegion || bothSidesInsertedAtRegionStart) {
            for (const colleagueUnit of regionOf(liveSide, regionStart, regionEnd, fromCurrent)) {
                mergedIndexesTheColleagueChanged.add(merged.length);
                merged.push(colleagueUnit);
            }
        }
        /**
         * EXAMPLE
         *   jointIndexBeforeRegion = 6, merged.length now 8 -> the live words of the region are merged[6..8)
         */
        const liveRegionUnits = merged.slice(jointIndexBeforeRegion);
        /**
         * CASES
         *   - desired touched, live untouched        -> add desired-side text unless the live context already has it
         *   - desired touched, live touched too      -> add desired-side text: the redline wins the region
         *   - desired untouched                      -> nothing to add
         */
        let desiredRegionUnitsWritten: MergedUnit[] = [];
        if (fromWanted.touched) {
            const incoming = regionOf(desiredSide, regionStart, regionEnd, fromWanted);
            /**
             * WHEN both sides changed the region to different words, including two
             * insertions of different words at the same point
             *   CONSEQUENCE
             *     the region is contested: a caller folding two redlines reads the count
             *     to know the merged text needs a decision instead of being written as is.
             *     The merged text still keeps both insertions (live first) as the
             *     mechanical result a single redline over a colleague's edit falls back to.
             *
             * WHY same-point insertions count
             *   Two cards adding different words at one spot ("…consent HW3-R1" and
             *   "…consent HW3-R2") were concatenated silently as "consent HW3-R1 HW3-R2"
             *   and credited; that is a collision for the fold to decide, not a merge.
             *
             * EXAMPLE
             *   live region "must", incoming "will"          -> contested
             *   live region "will", incoming "will"          -> the same change made twice, not contested
             *   live inserts "promptly", incoming "at once"  -> same point, different words: contested
             */
            if (fromCurrent.touched && !sameUnitWords(regionOf(liveSide, regionStart, regionEnd, fromCurrent), incoming)) contestedRegionCount += 1;
            /** The redline's words replace the colleague's in a shared region; at a shared insertion point the colleague's words were kept above and the redline's follow. */
            const redlineWinsOverColleague = fromCurrent.touched && !bothSidesInsertedAtRegionStart;
            const desiredRegionIsMissingFromLiveContext = !desiredRegionIsAlreadyPresentInLiveContext(incoming, regionStart, regionEnd);
            /**
             * WHEN the redline wins the region, or the live context does not already
             * contain these desired words
             *   CONSEQUENCE
             *     Append the desired-side units.
             *
             * FALLBACK
             *   A redline insertion the colleague's own change, written just before the
             *   point, already ends with -> skip it (a redline applied twice;
             *   `desiredRegionIsAlreadyPresentInLiveContext`).
             */
            if (redlineWinsOverColleague || desiredRegionIsMissingFromLiveContext) {
                merged.push(...incoming);
                desiredRegionUnitsWritten = incoming;
            }
        }
        /**
         * WHEN the region is one side's words inserted at a point where the other side
         * inserted nothing
         *   CONSEQUENCE
         *     the gaps either side of them are decided together (`decideGapsAroundInsertion`).
         *     Two insertions at one point keep each joint's own choice: the colleague's gap
         *     after its words, then the redline's gaps around its own.
         */
        const onlyOneSideInsertedHere = regionIsAnInsertionPoint && (liveRegionUnits.length === 0) !== (desiredRegionUnitsWritten.length === 0);
        if (onlyOneSideInsertedHere) {
            const insertedUnits = liveRegionUnits.length > 0 ? liveRegionUnits : desiredRegionUnitsWritten;
            decideGapsAroundInsertion(insertedUnits[0].side, insertedUnits, regionStart, jointIndexBeforeRegion);
        }
        originalCursor = regionEnd;
    }

    /**
     * The gap written at joint `jointIndex`, before `merged[jointIndex]`.
     *
     * CASES
     *   - decided around an insertion                      -> that gap
     *   - a gap a side wrote inside its own words          -> that gap, as written
     *   - a gap one of the texts holds there (`chosenGapAtJoint`) -> that gap
     *   - no text holds a gap there                        -> "": nothing is added between two
     *                                                         units no text held side by side, in
     *                                                         any script
     *
     * EXAMPLE, no text holds a gap
     *   original "", page "Owner shall approve.", card "Approval is required." -> both are
     *   inserted at the one point an empty paragraph has; the page ends at "approve." and the
     *   card starts at "Approval", and neither text holds anything between them:
     *   "Owner shall approve.Approval is required."
     */
    const gapAtJoint = (jointIndex: number): string => {
        const decidedGap = gapDecidedAtJoint.get(jointIndex);
        if (decidedGap !== undefined) return decidedGap;
        /**
         * EXAMPLE
         *   jointIndex 0         -> no unit on its left, the merged text's start
         *   jointIndex 3         -> merged[2] on its left, merged[3] on its right
         *   jointIndex = length  -> no unit on its right, the merged text's end
         */
        const leftUnit = jointIndex > 0 ? merged[jointIndex - 1] : null;
        const rightUnit = jointIndex < merged.length ? merged[jointIndex] : null;
        const joint = jointBetween(leftUnit, rightUnit, originalWordCount);
        if (typeof joint === "string") return joint;
        return chosenGapAtJoint(joint, original, liveSide, desiredSide) ?? "";
    };
    /**
     * EXAMPLE
     *   merged "本", "商品", "の" -> gap 0, "本", gap 1, "商品", gap 2, "の", gap 3:
     *   one gap before each unit and one after the last, so `<=` reaches joint `merged.length`
     */
    let textToWrite = "";
    for (let jointIndex = 0; jointIndex <= merged.length; jointIndex++) {
        textToWrite += gapAtJoint(jointIndex);
        if (jointIndex < merged.length) textToWrite += textOfMergedUnit(merged[jointIndex]);
    }
    /**
     * RETURNS
     *   The merged paragraph text, in the characters of the texts it took, and the
     *   count of contested regions.
     */
    return { textToWrite, contestedRegionCount };
}
